import { useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser'
import { ApiClient } from '@/lib/apiClient'
import { apiScopes, isApiScopeConfigured, silentRedirectUri } from '@/lib/msalConfig'
import { useHostContext } from '@/contexts/HostContext'
import { getTeamsAuthToken } from '@/lib/teamsHost'

export type ApiClientGate = 'ready' | 'not_signed_in' | 'not_configured'

/**
 * Marca, por pestaña, que ya se escaló una vez a interacción para el scope de
 * la API. Es el único freno contra un bucle de redirects: si el usuario vuelve
 * del redirect y el token silencioso sigue fallando, la segunda vez NO se
 * redirige — el error sube a la UI y se ve. `sessionStorage` (no
 * `localStorage`) porque el permiso de reintentar debe renovarse con cada
 * pestaña nueva, no arrastrarse indefinidamente.
 */
const INTERACTIVE_ESCALATION_KEY = 'wacner-sales.api-token.interactive-attempted'

function hasEscalated(): boolean {
  try {
    return window.sessionStorage.getItem(INTERACTIVE_ESCALATION_KEY) === '1'
  } catch {
    // Almacenamiento bloqueado: se asume escalado para no arriesgar un bucle.
    return true
  }
}

function markEscalated(): void {
  try {
    window.sessionStorage.setItem(INTERACTIVE_ESCALATION_KEY, '1')
  } catch {
    /* almacenamiento bloqueado */
  }
}

function clearEscalated(): void {
  try {
    window.sessionStorage.removeItem(INTERACTIVE_ESCALATION_KEY)
  } catch {
    /* almacenamiento bloqueado */
  }
}

/**
 * Token de acceso para la API de Wacner Sales en el host web.
 *
 * `redirectUri: silentRedirectUri` solo interviene cuando MSAL cae al iframe
 * oculto (caché vacía y refresh token no redimible). Sin él, ese iframe carga
 * la SPA completa y la renovación termina siempre en `timed_out`.
 *
 * No hay lock ni deduplicación propia: MSAL ya agrupa las peticiones
 * silenciosas concurrentes con la misma clave (cuenta + scopes + authority) en
 * `activeSilentTokenRequests`, así que las tres cargas simultáneas de /hoy
 * comparten un único viaje a la red. Añadir un lock aquí solo duplicaría esa
 * lógica.
 *
 * `InteractionRequiredAuthError` es la señal legítima de que el flujo
 * silencioso no puede resolverse: se escala una vez a `acquireTokenRedirect` y
 * el error se propaga igualmente, para que la UI no quede en blanco si la
 * navegación no llega a ocurrir. Cualquier otro error (incluido `timed_out`)
 * se propaga sin más: no es interacción lo que falta, y disfrazarlo de login
 * escondería el problema.
 */
async function acquireApiAccessToken(
  instance: IPublicClientApplication,
  account: AccountInfo
): Promise<string> {
  try {
    const result = await instance.acquireTokenSilent({
      ...apiScopes,
      account,
      redirectUri: silentRedirectUri,
    })
    clearEscalated()
    return result.accessToken
  } catch (error) {
    const needsInteraction = error instanceof InteractionRequiredAuthError
    const canRedirect = window.self === window.top

    if (needsInteraction && canRedirect && !hasEscalated()) {
      markEscalated()
      console.warn('[SCC][MSAL] silent token requires interaction — escalating once to redirect')
      void instance.acquireTokenRedirect({ ...apiScopes, account }).catch((redirectError) => {
        console.error('[SCC][MSAL] acquireTokenRedirect failed:', redirectError)
      })
    }

    throw error
  }
}

/**
 * Host-aware source of authenticated ApiClients — the single place that
 * knows how an access token for the Wacner Sales API is obtained in each
 * host:
 *
 * - web: MSAL `acquireTokenSilent` against the api://.../access_as_user
 *   scope, gated on a signed-in MSAL account (the AuthGate redirect flow).
 * - teams: Teams classic SSO — `authentication.getAuthToken()` returns a
 *   token issued directly for the API app (manifest webApplicationInfo
 *   resource), brokered by Teams' own session. No MSAL account exists in
 *   this host, and no VITE_MICROSOFT_API_SCOPE is needed: the resource
 *   comes from the Teams app manifest, not the env.
 *
 * Data hooks branch on `gate` exactly like they used to branch on
 * accounts[0]/isApiScopeConfigured, so their status contracts are
 * unchanged.
 */
export function useApiClient(): { gate: ApiClientGate; makeClient: () => ApiClient | null } {
  const { host } = useHostContext()
  const { instance, accounts } = useMsal()
  const account = accounts[0]

  const makeClient = useCallback(() => {
    if (host === 'teams') {
      return new ApiClient({ getAccessToken: getTeamsAuthToken })
    }
    if (!account) return null
    return new ApiClient({
      getAccessToken: () => acquireApiAccessToken(instance, account),
    })
  }, [host, account, instance])

  const gate: ApiClientGate =
    host === 'teams' ? 'ready'
      : !account ? 'not_signed_in'
        : !isApiScopeConfigured() ? 'not_configured'
          : 'ready'

  return { gate, makeClient }
}
